// src/auth/AuthProvider.js
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AuthContext } from './AuthContext';
import { fetchWishlist, syncWishlistAfterLogin } from '../store/slices/wishlistSlice';
import { fetchCart, clearCartState } from '../store/slices/cartSlice';
import api from '../api/client';
import { updateUser, changePassword, forgotPassword, resetPassword } from '../api/auth';
import { setAuthSession, getToken, updateStoredUser, clearAuthSession } from './authStorage';
import { WISHLIST_ENABLED } from '../config/features';

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);

      const { token, user: newUserData } = response.data;

      // Save token and user data (registration always persists, like a remembered login)
      setAuthSession(token, newUserData, true);

      // Update state
      setUser(newUserData);
      setIsAuthenticated(true);

      dispatch(clearCartState());

      if (WISHLIST_ENABLED && newUserData) {
        try {
          await dispatch(syncWishlistAfterLogin(newUserData.id)).unwrap();
        } catch (syncError) {
          console.error('Wishlist sync failed after registration:', syncError);
        }
        dispatch(fetchWishlist(newUserData.id));
      }
      dispatch(fetchCart());

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Registration failed'
      };
    }
  }, [dispatch]);

  // Login function
  // remember=true  -> localStorage (survives closing the browser)
  // remember=false -> sessionStorage (cleared when the browser/tab is closed)
  const login = useCallback(async (email, password, remember = true) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      const { token, user: userData } = response.data;

      // Save token and user data
      setAuthSession(token, userData, remember);

      // Update state
      setUser(userData);
      setIsAuthenticated(true);

      if (WISHLIST_ENABLED) {
        try {
          await dispatch(syncWishlistAfterLogin(userData.id)).unwrap();
        } catch (syncError) {
          console.error('Wishlist sync failed after login:', syncError);
        }
        dispatch(fetchWishlist(userData.id));
      }
      dispatch(fetchCart());

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Login failed'
      };
    }
  }, [dispatch]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call logout API
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API fails
    }

    // Clear stored session (both localStorage and sessionStorage)
    clearAuthSession();

    // Drop the chat widget's conversation too — it may hold this user's name/
    // email/phone from a submitted inquiry, and shouldn't carry over to whoever
    // uses this device next.
    document.dispatchEvent(new Event('dyna-chat-logout'));

    // Clear state
    setUser(null);
    setIsAuthenticated(false);

    // Rehydrate cart from local storage for guest mode
    dispatch(fetchCart());
  }, [dispatch]);

  // Update profile function
  const updateProfile = useCallback(async (userData) => {
    try {
      // Backend only returns a confirmation message, not the updated user —
      // merge the submitted fields into the existing user instead of trusting the response.
      await updateUser(userData);

      setUser((prev) => {
        const updatedUser = { ...prev, ...userData };
        updateStoredUser(updatedUser);
        return updatedUser;
      });

      return { success: true };
    } catch (error) {
      console.error('Update profile error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update profile'
      };
    }
  }, []);

  // Change password function
  const updatePassword = useCallback(async (passwordData) => {
    try {
      await changePassword(passwordData);
      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to change password'
      };
    }
  }, []);

  // Forgot password function
  const requestPasswordReset = useCallback(async (email) => {
    try {
      await forgotPassword({ email });
      return { success: true };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to send reset email'
      };
    }
  }, []);

  // Reset password function
  const resetUserPassword = useCallback(async (resetData) => {
    try {
      await resetPassword(resetData);
      return { success: true };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to reset password'
      };
    }
  }, []);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      const userData = response.data;

      // Update state
      setUser(userData);
      setIsAuthenticated(true);

      // Update stored copy with latest user data
      updateStoredUser(userData);
      if (WISHLIST_ENABLED) {
        try {
          await dispatch(syncWishlistAfterLogin(userData.id)).unwrap();
        } catch (syncError) {
          console.error('Wishlist sync failed during session restore:', syncError);
        }
        dispatch(fetchWishlist(userData.id));
      }
      dispatch(fetchCart());
    } catch (error) {
      console.error('Fetch user error:', error);
      // Clear invalid tokens
      clearAuthSession();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize auth state on app start
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    register,
    login,
    logout,
    fetchUser,
    updateProfile,
    updatePassword,
    requestPasswordReset,
    resetUserPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
